/**
 * Utilidades para detectar y manejar dominios, subdominios y hosts custom.
 */

const DEFAULT_MAIN_DOMAINS = ['orgcloud.io', 'www.orgcloud.io'];

export const normalizeHostname = (value?: string | null): string => {
  if (!value || typeof value !== 'string') return '';
  return value.trim().toLowerCase().replace(/\.$/, '');
};

export const normalizeCustomAppHostname = (value?: string | null): string => {
  const host = normalizeHostname(value);
  if (!host) return '';
  const withoutScheme = host.replace(/^https?:\/\//, '');
  const withoutPath = withoutScheme.split('/')[0]?.split('?')[0] || '';
  const withoutAuth = withoutPath.includes('@') ? withoutPath.split('@').pop() || '' : withoutPath;
  return withoutAuth.split(':')[0] || '';
};

const getConfiguredMainDomains = (): string[] => {
  const raw = process.env.REACT_APP_MAIN_DOMAINS;
  const configured = (raw || '')
    .split(',')
    .map((item) => normalizeHostname(item))
    .filter(Boolean);
  return configured.length ? configured : DEFAULT_MAIN_DOMAINS;
};

export const isLocalDevelopmentHost = (hostname?: string): boolean => {
  const normalized = normalizeHostname(hostname || window.location.hostname);
  return (
    normalized === 'localhost' ||
    normalized === '127.0.0.1' ||
    normalized === '::1' ||
    (normalized.length > 0 && !normalized.includes('.')) ||
    normalized.endsWith('.ts.net') ||
    /^\d+\.\d+\.\d+\.\d+$/.test(normalized)
  );
};

export const isMainDomain = (hostname?: string): boolean => {
  const normalized = normalizeHostname(hostname || window.location.hostname);
  if (!normalized || isLocalDevelopmentHost(normalized)) {
    return true;
  }
  return getConfiguredMainDomains().includes(normalized);
};

export const getSubdomain = (hostname?: string): string | null => {
  const normalized = normalizeHostname(hostname || window.location.hostname);
  if (!normalized || isLocalDevelopmentHost(normalized) || isMainDomain(normalized)) {
    return null;
  }

  const orgcloudBase = getConfiguredMainDomains()
    .filter((domain) => domain !== 'www.orgcloud.io')
    .find((domain) => normalized === domain || normalized.endsWith(`.${domain}`));

  if (!orgcloudBase || !normalized.endsWith(`.${orgcloudBase}`)) {
    return null;
  }

  const subdomain = normalized.slice(0, -(orgcloudBase.length + 1));
  if (!subdomain || subdomain === 'www' || subdomain.includes('.')) {
    return null;
  }
  return subdomain;
};

export const isCustomClientHostname = (hostname?: string): boolean => {
  const normalized = normalizeHostname(hostname || window.location.hostname);
  if (!normalized || isLocalDevelopmentHost(normalized) || isMainDomain(normalized)) {
    return false;
  }
  return !getSubdomain(normalized);
};

export const getTenantRoutingIdentifier = (hostname?: string): string | null => {
  const normalized = normalizeHostname(hostname || window.location.hostname);
  if (!normalized || isLocalDevelopmentHost(normalized) || isMainDomain(normalized)) {
    return null;
  }
  return getSubdomain(normalized) || normalized;
};

export const shouldResolveTenantByHost = (hostname?: string): boolean =>
  isCustomClientHostname(hostname || window.location.hostname);

export const getTenantStorageNamespace = (hostname?: string): string => {
  const normalized = normalizeHostname(hostname || window.location.hostname);
  if (!normalized) return 'default';
  if (isCustomClientHostname(normalized)) {
    return normalized;
  }
  return getSubdomain(normalized) || normalized;
};

export const clientMatchesCurrentHostname = (
  client:
    | {
        Subdomain?: string;
        CustomAppHostname?: string;
        Information?: { CustomAppHostname?: string };
      }
    | null
    | undefined,
  hostname?: string
): boolean => {
  if (!client) return false;
  const normalizedHost = normalizeHostname(hostname || window.location.hostname);
  const clientCustomHost = normalizeCustomAppHostname(
    client.CustomAppHostname || client.Information?.CustomAppHostname
  );
  if (clientCustomHost && clientCustomHost === normalizedHost) {
    return true;
  }
  const currentSubdomain = getSubdomain(normalizedHost);
  return Boolean(
    currentSubdomain &&
      client.Subdomain &&
      normalizeHostname(client.Subdomain) === currentSubdomain
  );
};

/**
 * Construye la URL de redirección hacia un subdominio específico de cliente.
 */
export const buildClientRedirectUrl = (
  clientSubdomain: string,
  path: string = '/admin'
): string => {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;

  if (isLocalDevelopmentHost(hostname)) {
    return `${protocol}//${hostname}${port ? ':' + port : ''}${path}`;
  }

  if (!clientSubdomain || clientSubdomain.trim() === '' || clientSubdomain.toLowerCase() === 'orgcloud') {
    const mainDomain =
      getConfiguredMainDomains().find((domain) => domain !== 'www.orgcloud.io') ||
      hostname.split('.').slice(-2).join('.');
    return `${protocol}//${mainDomain}${port ? ':' + port : ''}${path}`;
  }

  const configuredBase =
    getConfiguredMainDomains().find((domain) => domain !== 'www.orgcloud.io') ||
    hostname.split('.').slice(-2).join('.');

  return `${protocol}//${clientSubdomain}.${configuredBase}${path}`;
};
